import { createSelector } from '@reduxjs/toolkit';
import { selectBorderById } from '@ff-scene-plugins/photo-prints-redux/selectors/borderSelectors';
import { selectVariantById } from '@ff-scene-plugins/photo-prints-redux/selectors/variantSelectors';
import { selectGroupById } from '@ff-scene-plugins/photo-prints-redux/slices/groupsSlice';
import { selectAllPhotos, selectPhotoById } from '@ff-scene-plugins/photo-prints-redux/slices/photosSlice';
import { PhotoPrintsPdpState } from '@ff-scene-plugins/photo-prints-redux/store';
import { magic_effects } from '@ff-scene-plugins/photo-prints-shared/magic_effects';
import { PhotoPrintsJson } from '@ff-scene-plugins/photo-prints-shared/PhotoPrintsJson';
import { standard_effects } from '@ff-scene-plugins/photo-prints-shared/standard_effects';
import { toPaintable } from '@ff-scene-plugins/photo-prints-shared/toPaintable';
import { toPatternPaintable } from '@ff-scene-plugins/photo-prints-shared/toPatternPaintable';
import { toPhotoUrl } from '@ff-scene-plugins/photo-prints-shared/toPhotoUrl';
import { toSpaces } from '@ff-scene-plugins/photo-prints-shared/toSpaces';
import { uniqBy } from 'lodash';
import { selectTotalAmountInQueue } from './_redux-groups';
import { selectSizeByPhotoId } from './_redux-sizes';

export const selectUrlForPhotoById = createSelector([
    selectPhotoById,
    (state: PhotoPrintsPdpState) => state.auth.jwt
], (photo, jwt) => {
    return photo ? toPhotoUrl(photo, jwt) : undefined;
});

export const selectOriginalUrlForPhotoById = createSelector([
    selectPhotoById,
    (state: PhotoPrintsPdpState) => state.auth.jwt
], (photo, jwt) => {

    if (!photo) {
        return undefined;
    };

    const photoWithoutBorders = {
        ...photo,
        magicBorderId: undefined,
        magicBorderRngSeed: undefined,
        magicBorderAssetId: undefined,
        patternId: undefined
    };

    return toPhotoUrl(photoWithoutBorders, jwt);
});

export const selectFilledPhotoByIdWithoutBorders = createSelector([
    selectPhotoById
], (photo) => {

    if (!photo) {
        return photo;
    };

    const photoWithoutBorders = {
        ...photo,
        magicBorderId: undefined,
        magicBorderRngSeed: undefined,
        magicBorderAssetId: undefined,
        patternId: undefined
    };

    return photoWithoutBorders as PhotoPrintsJson.ProcessedPhoto;
});

export const selectFilterByPhotoId = createSelector([
    selectPhotoById
], photo => photo!.filter);

export const getPhotosStatus = createSelector([
    selectAllPhotos
], (photos): 'error' | 'pending' | 'ready' => {
    if (photos.some(p => p.status === 'error')) {
        return 'error';
    } else if (photos.some(p => p.status === 'saving')) {
        return 'pending';
    } else {
        return 'ready';
    }
});

type PhotoStatus = PhotoPrintsJson.Photo['status'];
export const selectPhotosWhereStatusIs = createSelector([
    (state: PhotoPrintsPdpState, status: PhotoStatus) => selectAllPhotos(state),
    (state: PhotoPrintsPdpState, status: PhotoStatus) => status
], (photos, status) => {
    return uniqBy(photos.filter(p => p.status === status), p => p.assetId);
});

export const selectAmountOfUploadsRemaining = createSelector([
    (state: PhotoPrintsPdpState) => selectTotalAmountInQueue(state),
    (state: PhotoPrintsPdpState) => selectPhotosWhereStatusIs(state, 'saving')
], (amountInQueue, savingPhotos) => {
    return amountInQueue + savingPhotos.length;
});

export const selectQuantityById = createSelector([
    selectPhotoById
], photo => photo!.quantity);

export const selectFilledPhotoById = createSelector([
    selectPhotoById
], (photo) => {
    return photo as PhotoPrintsJson.ProcessedPhoto;
});

export const selectPhotoStatusById = createSelector([
    selectFilledPhotoById
], (photo) => photo.status);

export const selectSpaces = createSelector([
    selectSizeByPhotoId,
    selectFilledPhotoById
], (size, photo) => {
    return toSpaces(size, photo);
});

export const selectPaintables = createSelector([
    selectFilledPhotoById,
    selectSizeByPhotoId,
    (state: PhotoPrintsPdpState) => state
], (photo, size, state) => {

    const group = selectGroupById(state, photo.groupId);

    const magicBorderId = group?.magicBorderId;
    const patternId = group?.patternId;
    const effectId = magicBorderId || patternId;

    const hasPattern = patternId && patternId !== "";
    const variant = selectVariantById(state, group?.variantId ?? '');
    const border = selectBorderById(state, variant?.borderId);
    const paintables = [];

    // Handle patterns (non-magic effects)
    if (hasPattern && effectId) {
        const allEffects = [...magic_effects, ...standard_effects];
        const frameEffect = allEffects.find(effect => effect.id === effectId);
        const tileSize = 55;

        if (frameEffect && !frameEffect.isMagic) {
            const patternPaintable = toPatternPaintable(photo.id, size, tileSize, {
                size: 0,
                color: '#FFF',
                frameId: effectId
            });

            paintables.push(patternPaintable);
        }
    }

    paintables.push(toPaintable(photo, size, state.auth.jwt, 'mockup', border?.value ? {
        // Double the border width to apply the same size on all sides
        size: size.borderWidth * 2,
        color: border.color ?? '#FFF'
    } : undefined, group));

    return paintables;
});